import type { ReactNode } from 'react'
import { useLocation, useNavigate } from 'react-router'
import { CURRENT_SPRINT, SPRINT_PLAN, hideWhenLocked, isUnlocked, sprintOf } from '@waago/api'
import { LockedScreen } from '@waago/ui'

/**
 * Holds back a screen that belongs to a later sprint.
 *
 * <p>Placed on the two wrappers every signed-in route already passes through
 * rather than on each route, so the router keeps saying what the product is
 * and this file stays the only place that knows about staging. Adding a screen
 * does not mean remembering to guard it.
 *
 * <p>Deciding at render time rather than by pruning the route table is what
 * lets a locked screen explain itself. A pruned route 404s, and someone who
 * followed a link from a list we deliberately left visible would have no idea
 * why.
 */
export function SprintGate({ children }: { children: ReactNode }) {
  const location = useLocation()
  const navigate = useNavigate()

  if (isUnlocked(location.pathname)) return <>{children}</>

  if (hideWhenLocked()) {
    return (
      <p role="status" className="p-8 text-center text-sm text-slate-500">
        Page introuvable.
      </p>
    )
  }

  const sprint = sprintOf(location.pathname)
  const plan = SPRINT_PLAN[sprint]
  return (
    <LockedScreen
      sprint={sprint}
      days={plan.days}
      scope={plan.title}
      currentSprint={CURRENT_SPRINT}
      onBack={() => void navigate('/', { replace: true })}
    />
  )
}
